import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { Check, Circle } from "lucide-react";
import type { SessionUser } from "@gatiq/shared";
import { Button, Spinner, TextInput, useToast } from "@/ui";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useAuth } from "@/auth/AuthProvider";
import { homeFor } from "@/auth/RequireAuth";
import { api, ApiClientError } from "@/lib/api";

/** Forced first-login password change (D-04). Staff-facing only — English. */
export function SetPassword() {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(orgSlug ? `/${orgSlug}/login` : "/login", { replace: true });
      return;
    }
    if (!user.mustChangePassword) {
      navigate(homeFor(user), { replace: true });
    }
  }, [user, loading, orgSlug, navigate]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSessionExpired(false);
    setSubmitting(true);
    try {
      const session = await api.post<SessionUser>("/auth/set-password", { password, confirm });
      setUser(session);
      navigate(homeFor(session), { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          // The restricted access token expired. The user must sign in again
          // with their temporary password to get a fresh token.
          setSessionExpired(true);
        } else {
          setFields(err.fields ?? {});
          toast.error(err.message);
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !user || !user.mustChangePassword) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface">
        <Spinner className="size-6 text-brand" />
      </div>
    );
  }

  const longEnough = password.length >= 8;
  const matches = confirm.length > 0 && password === confirm;

  return (
    <AuthLayout
      accent="one thing first."
      blurb="You signed in with a temporary password. Choose your own now — it takes a moment and nobody else will ever see it."
      headline="Almost there,"
      points={[
        "Your temporary password stops working immediately",
        "You will be signed out of any other device",
        "You can change it again whenever you like",
      ]}
    >
      <AuthCard
        subtitle="You are using a temporary password. Choose a new one."
        title="Set your password"
      >
        {sessionExpired ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm">
              <p className="font-medium text-ink">Your session has timed out</p>
              <p className="mt-1 text-muted">
                The sign-in link is only valid for 60 minutes. Close this tab, sign in again with your temporary password, and you will be sent straight back here.
              </p>
            </div>
            <a
              href={orgSlug ? `/${orgSlug}/login` : "/login"}
              className="block w-full rounded-lg bg-brand py-3 text-center font-title font-semibold text-on-brand shadow-sm transition hover:opacity-90"
            >
              Back to sign in
            </a>
          </div>
        ) : (
          <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <TextInput
                autoComplete="new-password"
                autoFocus
                error={fields.password}
                label="New password"
                onChange={(e) => setPassword(e.target.value)}
                required
                type="password"
                value={password}
              />
              <ul className="flex flex-col gap-0.5 text-sm">
                <li className={`flex items-center gap-1.5 ${longEnough ? "text-success" : "text-muted"}`}>
                  {longEnough ? (
                    <Check aria-hidden="true" className="size-4" />
                  ) : (
                    <Circle aria-hidden="true" className="size-4" />
                  )}
                  At least 8 characters
                </li>
                <li className="flex items-center gap-1.5 text-muted">
                  <Circle aria-hidden="true" className="size-4" />
                  Not your temporary password
                </li>
              </ul>
            </div>

            <TextInput
              autoComplete="new-password"
              error={fields.confirm}
              label="Confirm password"
              onChange={(e) => setConfirm(e.target.value)}
              required
              type="password"
              value={confirm}
            />

            <Button
              block
              disabled={!longEnough || !matches}
              loading={submitting}
              type="submit"
            >
            Continue
          </Button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
